import { MessageSquare } from 'lucide-react';
import { MobileCard } from '@/Components/shared/List/MobileCard';
import type { SavListItem } from '../types/sav.types';

function getStatusLabel(sav: SavListItem): string {
  if (sav.isV2 && sav.status) {
    switch (sav.status) {
      case 'ACTIVE': return 'Actif';
      case 'COMPLETED': return 'Terminé';
      case 'CANCELLED': return 'Annulé';
      default: return sav.status;
    }
  }
  if (sav.stateId) {
    switch (sav.stateId) {
      case 2: return 'Nouveau';
      case 3: return 'En attente bénéficiaire';
      case 4: return 'En attente gestionnaire';
      case 5: return 'Litige';
      case 6: return 'En attente garant';
      case 7: return 'À prendre en charge';
      case 8: return 'Pris en charge';
      case 9: return 'Sans suite';
      default: return 'Inconnu';
    }
  }
  return 'Inconnu';
}

function getStatusColorClass(sav: SavListItem): string {
  if (sav.isV2 && sav.status) {
    switch (sav.status) {
      case 'ACTIVE': return 'tw-bg-affinicar-lighter tw-text-affinicar-secondary';
      case 'COMPLETED': return 'tw-bg-green-100 tw-text-green-700';
      case 'CANCELLED': return 'tw-bg-gray-100 tw-text-gray-700';
      default: return 'tw-bg-gray-100 tw-text-gray-700';
    }
  }
  if (sav.stateId) {
    switch (sav.stateId) {
      case 2: return 'tw-bg-affinicar-lighter tw-text-affinicar-secondary';
      case 3: return 'tw-bg-orange-100 tw-text-orange-700';
      case 4: return 'tw-bg-affinicar-lighter tw-text-affinicar-secondary';
      case 5: return 'tw-bg-yellow-100 tw-text-yellow-700';
      case 6: case 7: return 'tw-bg-affinicar-lighter tw-text-affinicar-secondary';
      case 8: return 'tw-bg-green-100 tw-text-green-700';
      case 9: return 'tw-bg-gray-100 tw-text-gray-700';
      default: return 'tw-bg-gray-100 tw-text-gray-700';
    }
  }
  return 'tw-bg-gray-100 tw-text-gray-700';
}

function formatDate(date?: string | null): string {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

interface SavMobileCardsProps {
  data: SavListItem[];
  onRowClick?: (sav: SavListItem) => void;
}

export function SavMobileCards({ data, onRowClick }: SavMobileCardsProps) {
  return (
    <div className="tw-space-y-3">
      {data.map((sav) => {
        const beneficiary = [sav.beneficiaryFirstName, sav.beneficiaryLastName].filter(Boolean).join(' ') || '-';
        const vehicle = [sav.vehicleBrand, sav.vehicleModel].filter(Boolean).join(' ');

        return (
          <MobileCard
            key={sav.id}
            title={
              <div className="tw-flex tw-items-center tw-gap-2">
                <span>{sav.uniqueId || `#${sav.id}`}</span>
                {sav.isV2 && (
                  <span className="tw-px-1.5 tw-py-0.5 tw-text-[10px] tw-font-medium tw-rounded tw-bg-indigo-100 tw-text-indigo-700">V2</span>
                )}
                {sav.unreadGarantMessagesCount && sav.unreadGarantMessagesCount > 0 ? (
                  <span title="Messages non lus" className="tw-inline-flex tw-items-center tw-gap-0.5 tw-px-1.5 tw-py-0.5 tw-text-xs tw-font-bold tw-rounded-full tw-bg-blue-500 tw-text-white">
                    <MessageSquare className="tw-w-3 tw-h-3" />
                    {sav.unreadGarantMessagesCount}
                  </span>
                ) : null}
              </div>
            }
            subtitle={sav.companyName}
            status={
              <span className={`tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-rounded ${getStatusColorClass(sav)}`}>
                {getStatusLabel(sav)}
              </span>
            }
            fields={[
              { label: 'Bénéficiaire', value: beneficiary },
              { label: 'Véhicule', value: sav.vehicleRegistration || '-' },
              { label: 'Date panne', value: formatDate(sav.incidentDate) },
              { label: 'Déclaration', value: formatDate(sav.declarationDate) },
              ...(vehicle ? [{ label: 'Marque/Modèle', value: vehicle, fullWidth: true as const }] : []),
            ]}
            onClick={onRowClick ? () => onRowClick(sav) : undefined}
          />
        );
      })}
    </div>
  );
}
